// Пункт таблицы «Контроль»: бейдж статуса, источник, срок, основание решения ИИ и группы
// «протокол → раздел» (003, FR-036; 004, FR-008, FR-010, FR-027; приёмка 0.3.1, T042, T044).
// 010 (T027; FR-048): закрытый отметкой выполнения — «Закрыто · отметка» со значком CheckCheck
// и текстом ячейки в подсказке; в «Закрыто ИИ» такого пункта нет — его закрыло правило.
// 014 (T027; FR-004): пункты протоколов в режиме ведения — ключ строки itemId, группа — документ
// «П-N · название · таблица протокола»: листа недели у таких пунктов нет.
// 047 (DNS-01, DNS-02): «Без цвета» — тихий бейдж без обводки; остальные бейджи — как были.
import { clarifyReasonLabels, itemStatusLabels } from '@kp/contracts';
import type { ControlItem } from '@kp/contracts';
import { Badge, Tooltip } from '@kp/ui';
import {
  AlarmClock,
  CheckCheck,
  Circle,
  CircleCheck,
  CircleHelp,
  Flame,
  Pause,
  SearchCheck,
  Sparkles,
} from '@kp/ui/icons';
import { formatDate } from '../../app/format.ts';

/** Вероятность решения ИИ: два знака после запятой — «0,92». */
export function probabilityText(probability: number): string {
  return probability.toFixed(2).replace('.', ',');
}

/** Основание решения ИИ: текст примечания и текст ячейки срока, как в файле (FR-008). */
export function basisOf(item: ControlItem): string {
  const parts = [item.comment, item.dueRaw ? `Срок: ${item.dueRaw}` : null].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/** Источник в строке — только номер пункта: протокол и раздел — в заголовках групп. */
export function sourceOf(item: ControlItem): string {
  return item.number ? `№ ${item.number}` : `стр. ${item.row}`;
}

/** Срок в таблице: дата, а без даты — текст ячейки срока. */
export function dueText(item: ControlItem): string | null {
  return item.due ? formatDate(item.due) : item.dueRaw;
}

/** Пункт протокола в режиме ведения: строка ведёт в таблицу протокола к пункту (014, FR-004). */
export function isMaintained(item: ControlItem): boolean {
  return item.itemId !== undefined || item.documentMode === 'maintained';
}

/** Строка таблицы и боковой панели: пункт протокола в режиме ведения или версия и строка листа. */
export function itemKey(item: ControlItem): string {
  return item.itemId ?? `${item.versionId}:${item.row}`;
}

/** Подпись источника группы: лист недели версии или таблица протокола в режиме ведения. */
export function sheetLabelOf(item: ControlItem): string {
  return isMaintained(item) ? 'таблица протокола' : `лист ${item.sheetName}`;
}

/** Пункт на проверке: неуверенное свидетельство у красного или «Уточнить» (004, FR-027). */
export function isReview(item: ControlItem): boolean {
  return Boolean(item.evidenceReview) && (item.status === 'red' || item.status === 'clarify');
}

/** «Закрыто · отметка» — пункт закрыт отметкой выполнения в ячейке, без модели (010, FR-048). */
export const MARK_CLOSED_TEXT = 'Закрыто · отметка';

/** Класс тихого бейджа «Без цвета» и «Без цвета · пауза» (047, DNS-01). */
export const QUIET_BADGE = 'kp-badge-quiet';

/** Бейдж статуса: подпись и значок — статус читается не только цветом. */
export function StatusBadge({ item }: { item: ControlItem }) {
  if (item.closedBy === 'mark') {
    // Текст ячейки с отметкой — в подсказке; пометка получает фокус, подсказка — и с клавиатуры.
    const badge = (
      <Badge tone="ok" icon={CheckCheck}>
        {MARK_CLOSED_TEXT}
      </Badge>
    );
    return item.mark ? (
      <Tooltip content={`Отметка в ячейке: «${item.mark}»`}>
        <span
          className="kp-tip-badge"
          tabIndex={0}
          aria-label={`${MARK_CLOSED_TEXT}: «${item.mark}»`}
        >
          {badge}
        </span>
      </Tooltip>
    ) : (
      badge
    );
  }
  if (item.closedBy === 'evidence') {
    return (
      <Badge tone="ok" icon={Sparkles}>
        {item.evidenceProbability == null
          ? 'Закрыто · ИИ'
          : `Закрыто · ИИ ${probabilityText(item.evidenceProbability)}`}
      </Badge>
    );
  }
  if (isReview(item)) {
    return (
      <Badge tone="warn" icon={SearchCheck}>
        {`Проверить · ИИ ${probabilityText(item.evidenceProbability ?? 0)}${item.overdue ? ' · срок прошёл' : ''}`}
      </Badge>
    );
  }
  if (item.status === 'red') {
    return (
      <Badge tone="bad" icon={item.overdue ? AlarmClock : Flame}>
        {item.overdue ? `${itemStatusLabels.red} · просрочено` : itemStatusLabels.red}
      </Badge>
    );
  }
  if (item.status === 'clarify') {
    // Нейтральный: акцент — только у главного действия и выбора (T044); смысл — значок и подпись.
    return (
      <Badge icon={CircleHelp}>
        {item.clarify
          ? `${itemStatusLabels.clarify}: ${clarifyReasonLabels[item.clarify]}`
          : itemStatusLabels.clarify}
      </Badge>
    );
  }
  if (item.status === 'black') {
    return (
      <Badge tone="ok" icon={CircleCheck}>
        {itemStatusLabels.black}
      </Badge>
    );
  }
  // 047 (DNS-01): «Без цвета» — самый тихий бейдж таблицы статусов: заливка, подпись и значок
  // нейтрального, без обводки (control.css, kp-badge-quiet) — «Красный» не теряется среди серых.
  return item.paused ? (
    <Badge icon={Pause} className={QUIET_BADGE}>{`${itemStatusLabels.none} · пауза`}</Badge>
  ) : (
    <Badge icon={Circle} className={QUIET_BADGE}>
      {itemStatusLabels.none}
    </Badge>
  );
}

export interface SectionGroup {
  readonly key: string;
  readonly section: string;
  /** Имя таблицы раздела: «П-3 · Производство работ / Жилой дом № 1». */
  readonly label: string;
  readonly items: ControlItem[];
}

export interface ProtocolGroup {
  readonly key: string;
  /** «П-1 · Протокол оперативного совещания · лист 17.09.26» или «… · таблица протокола». */
  readonly title: string;
  readonly sections: SectionGroup[];
  readonly count: number;
  readonly overdue: number;
  readonly burning: number;
}

/** Ключ группы протокола: документ в режиме ведения или версия файла. */
function protocolKey(item: ControlItem): string {
  return isMaintained(item) ? `doc:${item.documentId}` : item.versionId;
}

/**
 * Группы «протокол → раздел» в порядке строк ответа: по протоколу — П-N и строка листа, по сроку —
 * сначала группы с ближайшим сроком.
 */
export function groupByProtocol(items: readonly ControlItem[]): ProtocolGroup[] {
  const protocols = new Map<string, { first: ControlItem; sections: Map<string, SectionGroup> }>();
  for (const item of items) {
    const key = protocolKey(item);
    let protocol = protocols.get(key);
    if (!protocol) {
      protocol = { first: item, sections: new Map() };
      protocols.set(key, protocol);
    }
    let section = protocol.sections.get(item.section);
    if (!section) {
      section = {
        key: `${key}|${item.section}`,
        section: item.section,
        label: `П-${item.documentNumber} · ${item.section}`,
        items: [],
      };
      protocol.sections.set(item.section, section);
    }
    section.items.push(item);
  }
  return [...protocols.entries()].map(([key, { first, sections }]) => {
    const rows = [...sections.values()].flatMap((section) => section.items);
    return {
      key,
      title: `П-${first.documentNumber} · ${first.documentTitle} · ${sheetLabelOf(first)}`,
      sections: [...sections.values()],
      count: rows.length,
      overdue: rows.filter((item) => item.group === 'overdue').length,
      burning: rows.filter((item) => item.group === 'burning').length,
    };
  });
}
